/* ─────────────────────────────────────────────────────────────────────────
   Pavos — Find your scent quiz.
   Port of the prototype's Quiz.jsx: six questions, tag-based scoring over
   the embedded product JSON, and a ranked three-candle shortlist.
   No backend; nothing leaves the page.
   ──────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /* ── Questions (verbatim from the prototype) ───────────────────────── */

  var QUESTIONS = [
    {
      key: 'who',
      question: 'Who is this candle for?',
      options: ['For me', 'For someone else', 'For my home', 'For a gift']
    },
    {
      key: 'time',
      question: 'When will it usually be lit?',
      options: ['Morning', 'Afternoon', 'Evening', 'Night', 'Whenever the room needs it']
    },
    {
      key: 'mood',
      question: 'What should the room feel like?',
      options: ['Clear', 'Warm', 'Romantic', 'Grounded', 'Bright', 'Dramatic', 'Soft']
    },
    {
      key: 'room',
      question: 'Which room?',
      options: ['Bedroom', 'Living room', 'Work desk', 'Bathroom', 'Dining table', 'Gift box']
    },
    {
      key: 'family',
      question: 'Choose your scent direction',
      options: [
        { label: 'Fresh', hint: 'Clean air, open windows, late mornings.' },
        { label: 'Floral', hint: 'Soft petals, warm skin, rooms that feel dressed.' },
        { label: 'Fruity', hint: 'Sunlit, easy, generous, instantly brighter.' },
        { label: 'Spiced', hint: 'Low light, slow evenings, warmth with a little edge.' },
        { label: 'Woody', hint: 'Grounded, quiet, textured, like a room with old books.' },
        { label: 'Ambered', hint: 'Golden, resinous, intimate, made for after dark.' },
        { label: 'Surprise me', hint: 'Let your answers decide.' }
      ]
    },
    {
      key: 'strength',
      question: 'How strong should it feel?',
      options: [
        { label: 'Soft', hint: 'A quiet presence at the edge of the room.' },
        { label: 'Present', hint: 'Noticeable the moment you walk in. Never loud.' },
        { label: 'Statement', hint: 'The room follows its lead.' }
      ]
    }
  ];

  /* ── Scoring maps ──────────────────────────────────────────────────── */

  var TIME_MAP = {
    Morning: ['Morning', 'Late Morning'],
    Afternoon: ['Afternoon'],
    Evening: ['Evening'],
    Night: ['Night'],
    'Whenever the room needs it': ['Morning', 'Late Morning', 'Afternoon', 'Evening', 'Night']
  };

  var MOOD_MAP = {
    Clear: ['Clean', 'Calm'],
    Warm: ['Warm'],
    Romantic: ['Romantic'],
    Grounded: ['Grounded'],
    Bright: ['Bright'],
    Dramatic: ['Dramatic'],
    Soft: ['Calm', 'Romantic']
  };

  var ROOM_MAP = {
    Bedroom: 'Bedroom',
    'Living room': 'Living Room',
    'Work desk': 'Desk',
    Bathroom: 'Bath',
    'Dining table': 'Dinner Table',
    'Gift box': 'Gift'
  };

  var STRENGTH_MAP = { Soft: 'Soft', Present: 'Medium', Statement: 'Statement' };
  var STRENGTH_ADJACENT = {
    Soft: ['Medium'],
    Medium: ['Soft', 'Statement'],
    Statement: ['Medium']
  };

  var RESULT_TITLES = {
    Clear: 'Your room is asking for something clear.',
    Warm: 'Your room is asking for something warm.',
    Romantic: 'Your room is asking for something romantic.',
    Grounded: 'Your room is asking for something grounded.',
    Bright: 'Your room is asking for something bright.',
    Dramatic: 'Your room is asking for something with a little drama.',
    Soft: 'Your room is asking for something soft.'
  };

  var TIME_PHRASES = {
    Morning: 'mornings',
    'Late Morning': 'late mornings',
    Afternoon: 'slow afternoons',
    Evening: 'evenings',
    Night: 'late nights'
  };

  var ROOM_PHRASES = {
    Bedroom: 'the bedroom',
    'Living Room': 'the living room',
    Desk: 'a work desk',
    Bath: 'the bath',
    'Dinner Table': 'the dinner table',
    Gift: 'a gift box'
  };

  /* ── Product data — derived from tags ──────────────────────────────── */

  function deriveProduct(raw) {
    var p = {
      id: raw.id,
      title: raw.title || '',
      url: raw.url || '#',
      price: raw.price || '',
      family: raw.family || null,
      notes: Array.isArray(raw.notes) ? raw.notes : [],
      bestLit: raw.bestLit || '',
      palette: raw.palette || {},
      img: raw.img || null,
      bestTimeSlot: null,
      moods: [],
      rooms: [],
      strength: null
    };

    (raw.tags || []).forEach(function (tag) {
      var i = String(tag).indexOf(':');
      if (i === -1) return;
      var key = String(tag).slice(0, i).trim().toLowerCase();
      var value = String(tag).slice(i + 1).trim();
      if (!value) return;
      if (key === 'family') p.family = value;
      else if (key === 'time' && !p.bestTimeSlot) p.bestTimeSlot = value;
      else if (key === 'mood') p.moods.push(value);
      else if (key === 'room') p.rooms.push(value);
      else if (key === 'strength') p.strength = value;
    });

    return p;
  }

  function readProducts(root) {
    var node = root.querySelector('[data-quiz-data]');
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(deriveProduct);
    } catch (err) {
      return [];
    }
  }

  /* ── Scoring (exact port of Quiz.jsx, over tag-derived fields) ─────── */

  function scoreProduct(product, answers) {
    var score = 0;

    if (answers.family && answers.family !== 'Surprise me' && product.family === answers.family) {
      score += 4;
    }

    var slots = TIME_MAP[answers.time] || [];
    if (slots.indexOf(product.bestTimeSlot) !== -1) score += 2;

    var wantedMoods = MOOD_MAP[answers.mood] || [];
    score += wantedMoods.filter(function (m) {
      return product.moods.indexOf(m) !== -1;
    }).length * 2;

    var room = ROOM_MAP[answers.room];
    if (room && product.rooms.indexOf(room) !== -1) score += 2;

    var targetStrength = STRENGTH_MAP[answers.strength];
    if (targetStrength) {
      if (product.strength === targetStrength) score += 2;
      else if ((STRENGTH_ADJACENT[targetStrength] || []).indexOf(product.strength) !== -1) score += 1;
    }

    if (
      (answers.who === 'For someone else' || answers.who === 'For a gift') &&
      product.rooms.indexOf('Gift') !== -1
    ) {
      score += 1;
    }

    return score;
  }

  /* ── "Why we chose it" — assembled from the actual matches ─────────── */

  function buildWhy(product, answers) {
    var parts = [];

    if (answers.family === 'Surprise me') {
      parts.push('a small surprise, as requested');
    } else if (product.family === answers.family) {
      parts.push(product.family + ', like you asked');
    }

    if (
      answers.time !== 'Whenever the room needs it' &&
      (TIME_MAP[answers.time] || []).indexOf(product.bestTimeSlot) !== -1
    ) {
      parts.push('made for ' + TIME_PHRASES[product.bestTimeSlot]);
    }

    var room = ROOM_MAP[answers.room];
    if (room && product.rooms.indexOf(room) !== -1) {
      var lead =
        product.strength === 'Soft'
          ? 'soft enough for'
          : product.strength === 'Statement'
            ? 'a statement for'
            : 'an easy fit for';
      parts.push(lead + ' ' + ROOM_PHRASES[room]);
    } else if (product.strength === STRENGTH_MAP[answers.strength]) {
      var alone = {
        Soft: 'kept deliberately soft',
        Medium: 'present without taking over',
        Statement: 'a proper statement'
      };
      parts.push(alone[product.strength]);
    }

    var wantedMoods = MOOD_MAP[answers.mood] || [];
    var moodMatch = wantedMoods.some(function (m) {
      return product.moods.indexOf(m) !== -1;
    });
    if (moodMatch) {
      parts.push('with the ' + String(answers.mood).toLowerCase() + ' mood you asked for');
    }

    var kept = parts.slice(0, 3);
    if (!kept.length) return 'Close to everything you asked for, in its own quiet way.';

    var head = kept[0];
    var rest = kept.slice(1);
    var lead2 = head.charAt(0).toUpperCase() + head.slice(1);
    if (!rest.length) return lead2 + '.';
    if (rest.length === 1) return lead2 + ' — ' + rest[0] + '.';
    return (
      lead2 + ' — ' + rest.slice(0, -1).join(', ') + ', and ' + rest[rest.length - 1] + '.'
    );
  }

  function computeResults(products, answers) {
    return products
      .map(function (product, index) {
        return { product: product, index: index, score: scoreProduct(product, answers) };
      })
      .sort(function (a, b) {
        return b.score - a.score || a.index - b.index;
      })
      .slice(0, 3)
      .map(function (entry) {
        return { product: entry.product, why: buildWhy(entry.product, answers) };
      });
  }

  /* ── Tiny DOM helpers ──────────────────────────────────────────────── */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = text;
    return node;
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── One quiz instance ─────────────────────────────────────────────── */

  function initQuiz(root) {
    if (!root || root.dataset.quizReady === 'true') return;
    root.dataset.quizReady = 'true';

    var products = readProducts(root);
    var screens = {
      intro: root.querySelector('[data-quiz-screen="intro"]'),
      steps: root.querySelector('[data-quiz-screen="steps"]'),
      results: root.querySelector('[data-quiz-screen="results"]')
    };
    var bar = root.querySelector('[data-quiz-bar]');
    var counter = root.querySelector('[data-quiz-count]');
    var stepWrap = root.querySelector('[data-quiz-step]');
    var questionEl = root.querySelector('[data-quiz-question]');
    var optionsEl = root.querySelector('[data-quiz-options]');
    var backBtn = root.querySelector('[data-quiz-back]');
    var beginBtn = root.querySelector('[data-quiz-begin]');
    if (!screens.intro || !screens.steps || !screens.results) return;

    var state = { step: 0, answers: {}, advancing: false };

    function scrollToTop() {
      var top = root.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({
        top: top > 0 ? top : 0,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    function showScreen(name) {
      Object.keys(screens).forEach(function (key) {
        screens[key].hidden = key !== name;
      });
      scrollToTop();
    }

    /* — Steps — */

    function renderStep() {
      var q = QUESTIONS[state.step];
      state.advancing = false;

      if (bar) bar.style.width = (((state.step + 1) / QUESTIONS.length) * 100) + '%';
      if (counter) counter.textContent = pad(state.step + 1) + ' / ' + pad(QUESTIONS.length);

      questionEl.textContent = q.question;
      optionsEl.innerHTML = '';
      optionsEl.classList.toggle('quiz__options--wide', q.options.length >= 4);

      q.options.forEach(function (opt) {
        var o = typeof opt === 'string' ? { label: opt } : opt;
        var selected = state.answers[q.key] === o.label;
        var button = el('button', 'quiz__option' + (selected ? ' is-selected' : ''));
        button.type = 'button';
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.appendChild(el('span', 'quiz__option-label', o.label));
        if (o.hint) button.appendChild(el('span', 'quiz__option-hint', o.hint));
        button.addEventListener('click', function () {
          choose(q.key, o.label, button);
        });
        optionsEl.appendChild(button);
      });

      /* fade / translate entrance */
      if (stepWrap) {
        stepWrap.classList.add('is-entering');
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            stepWrap.classList.remove('is-entering');
          });
        });
      }
    }

    function choose(key, label, button) {
      if (state.advancing) return;
      state.advancing = true;
      state.answers[key] = label;

      var pressed = optionsEl.querySelectorAll('.quiz__option');
      pressed.forEach(function (node) {
        node.classList.toggle('is-selected', node === button);
        node.setAttribute('aria-pressed', node === button ? 'true' : 'false');
      });

      window.setTimeout(function () {
        if (state.step < QUESTIONS.length - 1) {
          state.step += 1;
          renderStep();
          scrollToTop();
        } else {
          renderResults();
          showScreen('results');
        }
      }, prefersReducedMotion() ? 0 : 300);
    }

    /* — Results — */

    function stage(product, modifier) {
      var node = el('div', 'quiz__stage' + (modifier ? ' ' + modifier : ''));
      if (product.palette && product.palette.bg) {
        node.style.backgroundColor = product.palette.bg;
      }
      if (product.img) {
        var img = document.createElement('img');
        img.src = product.img;
        img.alt = product.title;
        img.loading = 'lazy';
        img.className = 'quiz__stage-img';
        node.appendChild(img);
      }
      return node;
    }

    function renderEmpty(container) {
      var empty = el('div', 'quiz__empty');
      empty.appendChild(el('h1', 'quiz__verdict', 'The candles are between batches.'));
      empty.appendChild(
        el(
          'p',
          'quiz__verdict-sub',
          'Nothing to score just yet — the shelf refills soon.'
        )
      );
      var link = el('a', 'btn btn--primary btn--full quiz__empty-link', 'Browse all candles');
      link.href = '/collections/all';
      var cta = el('div', 'quiz__empty-cta');
      cta.appendChild(link);
      empty.appendChild(cta);
      container.appendChild(empty);
    }

    function renderResults() {
      var container = screens.results;
      container.innerHTML = '';

      if (!products.length) {
        renderEmpty(container);
        return;
      }

      var results = computeResults(products, state.answers);
      var first = results[0];
      var runners = results.slice(1);

      /* verdict */
      var verdict = el('div', 'quiz__verdict-wrap');
      verdict.appendChild(el('p', 'eyebrow text-amber', 'Your shortlist'));
      verdict.appendChild(
        el(
          'h1',
          'quiz__verdict',
          RESULT_TITLES[state.answers.mood] || 'Your room has made up its mind.'
        )
      );
      verdict.appendChild(
        el(
          'p',
          'quiz__verdict-sub',
          'Three candles, chosen for your room, your hour, and the mood you asked for.'
        )
      );
      container.appendChild(verdict);

      /* rank 1 — featured split */
      var featured = first.product;
      var split = el('div', 'quiz__featured');
      split.appendChild(stage(featured, 'quiz__stage--featured'));

      var info = el('div', 'quiz__featured-info');
      info.appendChild(
        el('p', 'eyebrow text-smoke', 'No. 01' + (featured.family ? ' · ' + featured.family : ''))
      );
      info.appendChild(el('h2', 'quiz__featured-title', featured.title));
      if (featured.notes.length) {
        info.appendChild(el('p', 'quiz__notes', featured.notes.join(' · ')));
      }
      if (featured.bestLit) {
        info.appendChild(el('p', 'quiz__lit serif-italic text-amber', featured.bestLit));
      }
      var whyWrap = el('div', 'quiz__why hairline-top');
      whyWrap.appendChild(el('p', 'eyebrow text-smoke', 'Why we chose it'));
      whyWrap.appendChild(el('p', 'quiz__why-line serif-italic', first.why));
      info.appendChild(whyWrap);

      var ctaRow = el('div', 'quiz__featured-cta');
      var viewBtn = el('a', 'btn btn--primary btn--full', 'View Candle');
      viewBtn.href = featured.url;
      ctaRow.appendChild(viewBtn);
      if (featured.price) ctaRow.appendChild(el('p', 'quiz__price', featured.price));
      info.appendChild(ctaRow);

      split.appendChild(info);
      container.appendChild(split);

      /* ranks 2–3 */
      if (runners.length) {
        var grid = el('div', 'quiz__runners');
        runners.forEach(function (entry, i) {
          var product = entry.product;
          var card = el('a', 'group quiz__runner');
          card.href = product.url;

          var media = el('div', 'quiz__runner-media');
          media.appendChild(el('span', 'quiz__runner-rank', 'No. ' + pad(i + 2)));
          media.appendChild(stage(product, 'quiz__stage--runner'));
          card.appendChild(media);

          var headRow = el('div', 'quiz__runner-head');
          headRow.appendChild(el('h3', 'quiz__runner-title', product.title));
          if (product.price) headRow.appendChild(el('p', 'quiz__price', product.price));
          card.appendChild(headRow);

          if (product.family) card.appendChild(el('p', 'eyebrow quiz__runner-family text-smoke', product.family));
          if (product.notes.length) card.appendChild(el('p', 'quiz__notes', product.notes.join(' · ')));
          if (product.bestLit) card.appendChild(el('p', 'quiz__lit serif-italic text-amber', product.bestLit));

          card.appendChild(el('p', 'quiz__runner-why-label hairline-top', 'Why we chose it'));
          card.appendChild(el('p', 'quiz__runner-why', entry.why));
          card.appendChild(el('span', 'link-underline quiz__runner-view', 'View Candle'));

          grid.appendChild(card);
        });
        container.appendChild(grid);
      }

      /* quiet exits */
      var exits = el('div', 'quiz__exits hairline-top');
      var retake = el('button', 'link-underline quiz__exit', 'Retake the quiz');
      retake.type = 'button';
      retake.addEventListener('click', function () {
        state.answers = {};
        state.step = 0;
        showScreen('intro');
      });
      exits.appendChild(retake);
      exits.appendChild(el('span', 'quiz__exit-divider'));
      var browse = el('a', 'link-underline quiz__exit', 'Browse all candles');
      browse.href = '/collections/all';
      exits.appendChild(browse);
      container.appendChild(exits);
    }

    /* — Wiring — */

    if (beginBtn) {
      beginBtn.addEventListener('click', function () {
        if (!products.length) {
          renderResults();
          showScreen('results');
          return;
        }
        state.step = 0;
        renderStep();
        showScreen('steps');
      });
    }

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (state.advancing) return;
        if (state.step === 0) {
          showScreen('intro');
        } else {
          state.step -= 1;
          renderStep();
          scrollToTop();
        }
      });
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-quiz]').forEach(initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor: re-init when the section is re-rendered */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
